import { Fragment, useState } from "react";
import { GrButton, GrDialog, GrDialogProps, GrHelperMessage, GrStack, GrTextField, GrTypography, palette } from "@jnpmedi/grebob";
import { getFileNameWithoutExt } from "@newComponents/common/dropSection/DropSectionProgressBar";
import { FileUploadResultCard } from "@newComponents/common/FileUploadResultCard";
import { getFileUploadSizes, MvnMessage, resolveExtensionStrBy } from "@newLibs/utils";
import { UseBoolean } from "@newHooks/common/useBoolean";
import {
  ARCHIVER_UPLOAD_CALLBACK_INVALID_QUERY_KEYS,
  FileUploadProgressItemWithFile,
  useBulkFileUpload,
} from "@newHooks/common/useBulkFileUpload";
import { enqueueSnackbar } from "@newHooks/context/snackbar/notification.context";
import { useDocsPdfServiceInspectQuery } from "@newHooks/query/serviceInspect/docsPdfServiceInspect.query";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { DOCS_DOCUMENT_LANG, DOCS_FOLDER_LANG } from "@maven-msg/lang/docs";
import { DOCS_COMMON_LANG } from "@maven-msg/lang/docs/common";
import { MyOrganization } from "@maven-rest/auth/Organization.dto";
import { FolderType } from "@maven-rest/docs/Folder.dto";
import { FolderFileCriteria, StorageUsage } from "@maven-rest/docs/FolderFile.dto";

const DEFAULT_SINGLE_UPLOAD_FILEINFO = {
  title: "",
  version: FolderFileCriteria.FOLDER_FILE_DEFAULT_VERSION,
};

interface AddArchiverDocumentDialogProps {
  open: UseBoolean;
  organization: MyOrganization;
  storageUsage: StorageUsage;
  folderId?: bigint;
  size?: GrDialogProps["size"];
}

export function AddArchiverDocumentDialog({ open, organization, storageUsage, folderId, size = "xs" }: AddArchiverDocumentDialogProps) {
  const [singleFileInfo, setSingleFileInfo] = useState<{ title: string; version: string }>({ ...DEFAULT_SINGLE_UPLOAD_FILEINFO });

  const { uploadSizeLimitInMB, uploadSizeLimitInMBForHostOS } = getFileUploadSizes(organization?.licenses || []);

  const {
    fileInputRef,
    fileUploadList,
    isCreateDocumentFilesLoading,
    isCreatePresignedPostUrlBulkLoading,
    creatableFileLength,
    handleChangeFileList,
    handleAttachmentRowDelete,
    createDocumentFilesRequest,
  } = useBulkFileUpload({
    organizationId: organization.id,
    uploadSizeLimitInMB,
    uploadSizeLimitInMBForHostOS,
    folderType: FolderType.ARCHIVER,
    folderId,
    invalidQueryKeys: ARCHIVER_UPLOAD_CALLBACK_INVALID_QUERY_KEYS,
    onFolderFileCreateSuccess: handleCloseDialog,
  });

  const isMultipleFileUpload = fileUploadList.length > 1;
  const isSingleFileUpload = fileUploadList.length === 1;
  const isSingleFileInvalid = isSingleFileUpload && (!singleFileInfo.title.trim().length || !singleFileInfo.version.trim().length);
  const isDialogConfirmDisable = !fileUploadList.length || isSingleFileInvalid || fileUploadList.some((file) => file.status !== "DONE");
  const isFileUploadActionLoading = isCreateDocumentFilesLoading || isCreatePresignedPostUrlBulkLoading;

  useDocsPdfServiceInspectQuery();

  function handleCloseDialog() {
    open.onFalse();
    setSingleFileInfo({ ...DEFAULT_SINGLE_UPLOAD_FILEINFO });
    fileInputRef.current = null;
  }

  const handleFileUploadButtonClick = () => {
    if (isFileUploadActionLoading) {
      return;
    }

    fileInputRef.current?.click();
  };

  const handleFileInputChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = evt.currentTarget.files;

    if (!fileList || !fileList.length) {
      enqueueSnackbar({ message: MvnMessage(DOCS_COMMON_LANG.EMPTY_FILE_WARN), severity: "warning" });
      return;
    }

    const fileArray = Array.from(fileList);

    if (fileArray.length === 1) {
      setSingleFileInfo({
        title: getFileNameWithoutExt(fileArray[0].name.normalize("NFC")).trim().substring(0, FolderFileCriteria.FOLDER_FILE_NAME_MAXLEN),
        version: FolderFileCriteria.FOLDER_FILE_DEFAULT_VERSION,
      });
    }

    handleChangeFileList(Array.from(fileList));
    evt.currentTarget.value = "";
  };

  const handleConfirmUploadArchiverDocument = () => {
    if (!creatableFileLength) {
      enqueueSnackbar({ message: MvnMessage("Not available creatable"), severity: "warning" });
      return;
    }

    if (creatableFileLength > FolderFileCriteria.FOLDER_FILE_BULK_UPLOAD_MAX_COUNT) {
      enqueueSnackbar({ message: MvnMessage("Max Count Exceeded available creatable"), severity: "warning" });
      return;
    }

    createDocumentFilesRequest(isSingleFileUpload ? singleFileInfo : undefined);
  };

  const handleResultCardDelete = (index: number) => {
    if (fileUploadList.length > 2) {
      handleAttachmentRowDelete(index);
      return;
    }

    if (fileUploadList.length === 1) {
      setSingleFileInfo((prev) => ({ ...prev, title: "" }));
      handleAttachmentRowDelete(index);
    }

    const remainFileUpload = fileUploadList.filter((_, idx) => idx !== index)[0];

    if (!!remainFileUpload) {
      setSingleFileInfo((prev) => ({
        ...prev,
        title: getFileNameWithoutExt(remainFileUpload.name?.trim()).substring(0, FolderFileCriteria.FOLDER_FILE_NAME_MAXLEN),
      }));
    }

    handleAttachmentRowDelete(index);
  };

  const renderFileUploadRow = (req: { fileUpload: FileUploadProgressItemWithFile; index: number }) => {
    const { fileUpload, index } = req;
    const { name, file, status, progress } = fileUpload;
    return (
      <FileUploadResultCard
        key={`${name}-${index}`}
        fileType={file.type?.toLowerCase()}
        fileName={name}
        onReset={() => handleResultCardDelete(index)}
        uploadProgressOptions={{ status, progress }}
        style={{ minHeight: "32px", backgroundColor: palette["white"] }}
      />
    );
  };

  return (
    <GrDialog
      preventCloseOnOutsideClick
      title={MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_UPLOAD_FILE_DIALOG_HEADER)}
      open={open.value}
      onConfirm={handleConfirmUploadArchiverDocument}
      onClose={handleCloseDialog}
      confirmButton={{
        children: MvnMessage(COMMON_LANG.OK_ALTER),
        disabled: isDialogConfirmDisable,
        isLoading: isFileUploadActionLoading,
      }}
      cancelButton={{ children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_CANCEL_NOUN) }}
      size={size}
    >
      <GrStack flexDirection="column" gap={18} padding="6px 0 0 0">
        {/* NOTE: 다중 업로드의 경우 파일 이름 및 DEFAULT Version (1.0) 으로 벌크 생성(Table DnD 또한 동일 정책), 단일파일은 변경 가능 */}
        <GrTextField
          required
          fullWidth
          autoFocus
          disabled={isMultipleFileUpload}
          label={{ text: MvnMessage(DOCS_COMMON_LANG.NAME_NOUN) }}
          placeholder={
            isSingleFileUpload || !fileUploadList.length
              ? MvnMessage(DOCS_COMMON_LANG.NAME_PLACEHOLDER)
              : MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_MULTIPLE_UPLOAD_NAME_PLACEHOLDER)
          }
          value={isMultipleFileUpload ? "" : singleFileInfo.title}
          onChange={(evt) => setSingleFileInfo((prev) => ({ ...prev, title: evt.target.value }))}
          maxLength={FolderFileCriteria.FOLDER_FILE_NAME_MAXLEN}
        />
        <GrTextField
          required
          fullWidth
          value={isMultipleFileUpload ? FolderFileCriteria.FOLDER_FILE_DEFAULT_VERSION : singleFileInfo.version}
          disabled={isMultipleFileUpload}
          maxLength={FolderFileCriteria.FOLDER_FILE_VERSION_MAXLEN}
          label={{ text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_VERSION_LABEL) }}
          onChange={(evt) => setSingleFileInfo((prev) => ({ ...prev, version: evt.target.value }))}
          placeholder={MvnMessage(DOCS_COMMON_LANG.VERSION_PLACEHOLDER)}
        />

        <GrStack flexDirection="column" gap={4}>
          <GrStack gap={2}>
            <GrTypography type="Content.Caption02" color="grey070">
              {MvnMessage(DOCS_COMMON_LANG.FILE_NOUN)}
            </GrTypography>
            <GrTypography type="Content.Caption02" color="red050">
              *
            </GrTypography>
          </GrStack>

          <GrStack flexDirection="column" gap={6}>
            {isSingleFileUpload && renderFileUploadRow({ fileUpload: fileUploadList[0], index: 0 })}

            {isMultipleFileUpload && (
              <GrStack
                key="multiple-files-upload-container"
                flexDirection="column"
                padding="8px"
                backgroundColor={palette.grey010}
                style={{ border: `1px solid ${palette.grey025}`, borderRadius: 6 }}
                maxHeight="220px"
                overflow="auto"
                gap={6}
              >
                {fileUploadList.map((fileUpload, index) => renderFileUploadRow({ fileUpload, index }))}
              </GrStack>
            )}
          </GrStack>

          <Fragment>
            <GrStack gap={6} alignItems="center">
              <GrButton
                color="grey"
                isLoading={isFileUploadActionLoading}
                startIcon={isCreateDocumentFilesLoading ? undefined : "upload"}
                disabled={fileUploadList.length === FolderFileCriteria.FOLDER_FILE_BULK_UPLOAD_MAX_COUNT}
                style={{ width: "fit-content" }}
                variant="outlined"
                onClick={handleFileUploadButtonClick}
              >
                {MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_UPLOAD_FILE_COUNT_LABEL, {
                  current: fileUploadList.length,
                  limit: FolderFileCriteria.FOLDER_FILE_BULK_UPLOAD_MAX_COUNT,
                })}
              </GrButton>
              <GrTypography type="Content.Caption02" color="grey060">
                {MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_UPLOAD_SIZE_HELPER_TEXT, {
                  max: uploadSizeLimitInMBForHostOS,
                })}
              </GrTypography>
            </GrStack>
            <GrHelperMessage
              hasIcon
              error={false}
              text={MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_UPLOAD_EXTENSION_HELPER_TEXT, {
                ext: resolveExtensionStrBy(FolderType.ARCHIVER),
              })}
            />
          </Fragment>
        </GrStack>
        <input
          ref={fileInputRef}
          hidden
          multiple
          type="file"
          accept={FolderFileCriteria.FOLDER_FILE_ARCHIVER_UPLOAD_ACCEPT}
          onChange={handleFileInputChange}
        />
      </GrStack>
    </GrDialog>
  );
}
